import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'
import type {
    AirCancelInfo,
    CancellationMessages,
    CancellationStep,
    CancelReason,
    CancelTripApiData,
    LoadStatus,
    SegmentRefund
} from '@/src/types/cancellation'

interface CancellationState {
    currentPage: CancellationStep
    loadStatus: LoadStatus
    // Selections
    travellers: string[]
    cancelReason: CancelReason
    // Cached step data (0 = cancelInfo, 1 = refundInfo) so backward nav restores without refetch
    cancelInfo: AirCancelInfo | null
    refundInfo: AirCancelInfo | null
    contextData: AirCancelInfo | null
    // Reasons
    cancellationReasons: CancelReason[]
    reasonsByFlight: Record<string, CancelReason[]>
    // Flags / meta
    currency: string
    fareType: string | null
    nonGuaranteed: boolean | null
    showWalletFlag: boolean | null
    openTicketBanner: boolean
    segmentDetail: SegmentRefund[]
    isCancelled: boolean
    // Derived messages
    messages: CancellationMessages
    // Confirmation
    cancelConfirmationMessages: string[]
    apiData: CancelTripApiData | null
}

export const initialMessages: CancellationMessages = {
    noticeMsg: [],
    validationMsg: null,
    validationMsgUrlText: null,
    ezcancelMsg: '',
    ezcancelProMsg: '',
    ezcancelProNote: '',
    ezcancelPlusMsg: '',
    ezcancelPlusUrl: '',
    ezcancelPlusUrlText: '',
    ezcancelErrorMsg: null,
    isCancelCTAChange: false
}

const emptyReason: CancelReason = { reason: '', reasonCode: '', docUpload: false }

const initialState: CancellationState = {
    currentPage: 0,
    loadStatus: 'LOADING',
    travellers: [],
    cancelReason: emptyReason,
    cancelInfo: null,
    refundInfo: null,
    contextData: null,
    cancellationReasons: [],
    reasonsByFlight: {},
    currency: 'INR',
    fareType: null,
    nonGuaranteed: null,
    showWalletFlag: null,
    openTicketBanner: false,
    segmentDetail: [],
    isCancelled: false,
    messages: initialMessages,
    cancelConfirmationMessages: [],
    apiData: null
}

export const cancellationSlice = createSlice({
    name: 'cancellation',
    initialState,
    reducers: {
        resetCancellation: () => initialState,
        setLoadStatus: (state, action: PayloadAction<LoadStatus>) => {
            state.loadStatus = action.payload
        },
        setCurrentPage: (state, action: PayloadAction<CancellationStep>) => {
            state.currentPage = action.payload
        },
        setTravellers: (state, action: PayloadAction<string[]>) => {
            state.travellers = action.payload
        },
        setCancelReason: (state, action: PayloadAction<CancelReason>) => {
            state.cancelReason = action.payload
        },
        setCancellationReasons: (state, action: PayloadAction<CancelReason[]>) => {
            state.cancellationReasons = action.payload
        },
        setReasonsForFlight: (state, action: PayloadAction<{ flightId: string; reasons: CancelReason[] }>) => {
            state.reasonsByFlight[action.payload.flightId] = action.payload.reasons
        },
        // Step 0 loaded
        setCancelInfoData: (
            state,
            action: PayloadAction<{
                data: AirCancelInfo
                currency: string
                fareType: string | null
                nonGuaranteed: boolean | null
                showWalletFlag: boolean | null
                openTicketBanner: boolean
                segmentDetail: SegmentRefund[]
                reasons: CancelReason[]
            }>
        ) => {
            const p = action.payload
            state.cancelInfo = p.data
            state.contextData = p.data
            state.currency = p.currency
            state.fareType = p.fareType
            state.nonGuaranteed = p.nonGuaranteed
            state.showWalletFlag = p.showWalletFlag
            state.openTicketBanner = p.openTicketBanner
            state.segmentDetail = p.segmentDetail
            state.cancellationReasons = p.reasons
            state.reasonsByFlight = { trip: p.reasons }
        },
        // Step 1 loaded (refund compute)
        setRefundInfoData: (
            state,
            action: PayloadAction<{
                data: AirCancelInfo
                currency: string
                fareType: string | null
                nonGuaranteed: boolean | null
            }>
        ) => {
            state.refundInfo = action.payload.data
            state.contextData = action.payload.data
            state.currency = action.payload.currency
            state.fareType = action.payload.fareType
            if (action.payload.nonGuaranteed !== null) {
                state.nonGuaranteed = action.payload.nonGuaranteed
            }
        },
        // Restore cached page data on backward nav
        restorePage: (state, action: PayloadAction<CancellationStep>) => {
            const page = action.payload
            state.contextData = page === 0 ? state.cancelInfo : state.refundInfo
            state.currentPage = page
        },
        setMessages: (state, action: PayloadAction<CancellationMessages>) => {
            state.messages = action.payload
        },
        setCancelled: (
            state,
            action: PayloadAction<{
                apiData: CancelTripApiData
                cancelConfirmationMessages: string[]
                fareType: string | null
                data: AirCancelInfo | null
            }>
        ) => {
            state.isCancelled = true
            state.apiData = action.payload.apiData
            state.cancelConfirmationMessages = action.payload.cancelConfirmationMessages
            state.fareType = action.payload.fareType
            state.contextData = action.payload.data
            state.currentPage = 2
        }
    }
})

export const {
    resetCancellation,
    setLoadStatus,
    setCurrentPage,
    setTravellers,
    setCancelReason,
    setCancellationReasons,
    setReasonsForFlight,
    setCancelInfoData,
    setRefundInfoData,
    restorePage,
    setMessages,
    setCancelled
} = cancellationSlice.actions

export const selectCancellation = (state: RootState) => state.cancellation

export default cancellationSlice.reducer
